import { compact } from "lodash";
import ReactMarkdown from "react-markdown";
import { AskAiChatMessageFragmentFragment } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { sanitizeMarkdown } from "@helpers/string";

import { OverviewUserPlaceholderType } from "./ask-ai-placeholders";

const AskAIEmbedOverviewUser = ({
  overviewUser,
}: {
  overviewUser:
    | NonNullable<
        NonNullable<
          NonNullable<AskAiChatMessageFragmentFragment["outputItems"]>[number]
        >["overviewUser"]
      >
    | OverviewUserPlaceholderType;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const summaries = compact([
    (organization.featureFlags.goals || true) &&
      overviewUser.goalProgress && {
        label: `${label("goal", { titleCase: true })} Progress`,
        value: overviewUser.goalProgress,
        icon: <TopicflowIcon type="goal" className="w-4 h-4 text-gray-400" />,
      },
    organization.featureFlags.actionItems &&
      overviewUser.actionItemProgress && {
        label: `${label("action_item", { titleCase: true })} Progress`,
        value: overviewUser.actionItemProgress,
        icon: <TopicflowIcon type="check" className="w-4 h-4 text-gray-400" />,
      },
    organization.featureFlags.feedbacks &&
      overviewUser.feedbackUpdates && {
        label: `${label("feedback", { titleCase: true })} Updates`,
        value: overviewUser.feedbackUpdates,
        icon: (
          <TopicflowIcon type="feedback" className="w-4 h-4 text-gray-400" />
        ),
      },
    organization.featureFlags.recognitions &&
      overviewUser.recognitionsUpdates && {
        label: `${label("recognition", { titleCase: true })} Updates`,
        value: overviewUser.recognitionsUpdates,
        icon: <TopicflowIcon type="star" className="w-4 h-4 text-gray-400" />,
      },
    organization.featureFlags.meetings &&
      overviewUser.meetingUpdates && {
        label: `${label("meeting", { titleCase: true })}`,
        value: overviewUser.meetingUpdates,
        icon: (
          <TopicflowIcon type="meeting" className="w-4 h-4 text-gray-400" />
        ),
      },
  ]);
  return (
    <div className="w-full flex flex-col gap-4">
      {summaries.map((summary) => (
        <div key={summary.label} className="flex gap-2">
          <div className="mt-0.5">{summary.icon}</div>
          <div className="flex flex-col gap-1">
            <div className="font-medium text-gray-800">{summary.label}</div>
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown>{sanitizeMarkdown(summary.value)}</ReactMarkdown>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AskAIEmbedOverviewUser;
